/* eslint-disable */
import { ArrowDown, ArrowUp, MoreVerticalIcon, MoveRight, Plus } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Button } from '../../Components/ui/button'
import {
  ArrowCircleDownOutlined,
  ArrowCircleUpOutlined,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Refresh,
} from '@mui/icons-material'
import { MutualFundsOrders } from '../../store/MFStore'
import { useRecoilRefresher_UNSTABLE, useRecoilState, useRecoilValueLoadable } from 'recoil'
import Loader from '../../Components/Loader/Loader'
import { Card, CardContent, CardHeader, CardTitle } from '../..//Components/ui/card'
import env from '../../env/env'
import { toCurrency } from '../../utils/CurrencyUtil'
import { Link, useNavigate } from 'react-router-dom'
import { MFBasketHoldings } from '../../store/MFBasketsStore'
import { Separator } from '../../Components/ui/separator'
import MFInvestmentSkeleton from '../../Components/SkeletonLoaders/MFInvestmentSkeleton'
import { EMPTY_STATE_ILLUSTRATIONS } from '../../Components/EmptyState/SIPsEmpty'
import ApiErrorState from '../../Components/Errors/ApiErrorState'
import basketRowIcon from '../../assets/SVGS/fabits-basket-row-icon.svg'
import {
  Menubar,
  MenubarContent,
  MenubarItem,
  MenubarMenu,
  MenubarTrigger,
} from '../../Components/ui/menubar'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../Components/ui/alert-dialog'
import { post } from '../../http/restservice'
import { userDetailsState } from '../LoginState'
import { useModal } from '../../hooks/use-modal-store'
import PostHogTracker from '../../utils/PostHogTracker'
import { AnimatedDisclosure } from '../../Components/ui/animated-disclosure'

const getBasketGroupKey = (order) =>
  order?.customerBasketInvestmentId ?? order?.customerBasketName ?? 'unknown-basket'

const getHoldingDedupeKey = (order) => {
  const basketKey = getBasketGroupKey(order)
  const fundKey =
    order?.isincd ??
    order?.isin ??
    order?.schemeCode ??
    order?.amcCode ??
    order?.legalName ??
    order?.schemeName ??
    'unknown-fund'
  const orderKey =
    order?.customerBasketOrderNo ??
    order?.basketOrderId ??
    order?.orderId ??
    order?.orderNo ??
    order?.folioNo ??
    ''
  const valueKey = [order?.netUnits, order?.netInvestedAmount, order?.currentNav]
    .map((value) => value ?? '')
    .join('|')

  return `${basketKey}|${fundKey}|${orderKey}|${valueKey}`
}

const dedupeHoldings = (holdings = []) => {
  const seen = new Set()

  return holdings.filter((order) => {
    const key = getHoldingDedupeKey(order)
    if (seen.has(key)) return false

    seen.add(key)
    return true
  })
}

const groupByBasketId = (orders = []) =>
  orders.reduce((acc, order) => {
    const basketKey = getBasketGroupKey(order)

    if (!acc[basketKey]) {
      acc[basketKey] = []
    }
    acc[basketKey].push(order)

    return acc
  }, {})

const MFBasketsInvestments = ({ emptyStateVariant = 'goals' }) => {
  const navigate = useNavigate()
  const orders = useRecoilValueLoadable(MFBasketHoldings)

  const [expandedIndex, setExpandedIndex] = useState(null)
  const refreshOrder = useRecoilRefresher_UNSTABLE(MutualFundsOrders)
  const [userDetails] = useRecoilState(userDetailsState)
  const userData = JSON.parse(localStorage.getItem('userData'))
  const clientCode = userDetails ? userDetails?.uid?.split('-')[0] : userData?.uid?.split('-')[0]
  const { onOpen } = useModal()
  const emptyStateImage =
    EMPTY_STATE_ILLUSTRATIONS[emptyStateVariant] || EMPTY_STATE_ILLUSTRATIONS.goals
  const basketHoldings = useMemo(() => {
    if (orders.state !== 'hasValue' || !Array.isArray(orders?.contents?.holdings)) {
      return []
    }

    return dedupeHoldings(orders.contents.holdings)
  }, [orders?.contents?.holdings, orders.state])
  const groupedOrders = useMemo(() => groupByBasketId(basketHoldings), [basketHoldings])

  const getTotalUnits = () => {
    return basketHoldings.reduce((acc, order) => {
      return acc + Number(order.netUnits)
    }, 0)
  }

  const getTotalAmount = () => {
    return basketHoldings.reduce((acc, order) => {
      return acc + Number(order.currentNav * order.netUnits)
    }, 0)
  }

  const getTotalInvestedAmount = () => {
    return basketHoldings.reduce((acc, order) => {
      return acc + Number(order.netInvestedAmount)
    }, 0)
  }

  const getBasketCurrentAmount = (basketOrders) => {
    return basketOrders.reduce((acc, order) => {
      return acc + Number(order.currentNav * order.netUnits)
    }, 0)
  }

  const getBasketInvestedAmount = (basketOrders) => {
    return basketOrders.reduce((acc, order) => {
      return acc + Number(order.netInvestedAmount)
    }, 0)
  }

  function truncateToTwoDecimals(num) {
    return Math.trunc(num * 100) / 100
  }

  const calculateParcentage = (invested, returns) => {
    const result = ((returns - invested) / invested) * 100
    return truncateToTwoDecimals(result)
  }

  const getStatus = (currentStatus) => {
    const statusMapping = {
      allotment: 'Allotted',
      partial: 'Partial',
      failed: 'Failed',
      processed: 'In Progress',
    }

    // Convert currentStatus to lowercase for case-insensitive matching
    const lowerCaseStatus = currentStatus.toLowerCase()

    // Iterate over the keys in statusMapping
    for (const keyword in statusMapping) {
      if (lowerCaseStatus.includes(keyword)) {
        return statusMapping[keyword]
      }
    }

    // Default return value if no keywords are matched
    return 'Unknown Status'
  }

  const getReturns = () => {
    const total = getTotalAmount()
    const invested = getTotalInvestedAmount()

    // Calculate returns in percentage
    const returns = ((total - invested) / invested) * 100

    return returns < 0 ? toCurrency(returns) : toCurrency(returns)
  }

  const handleMFBasketRedeem = (basket) => {
    // console.log('🚀 ~ handleMFBasketRedeem ~ basket:', basket)
    navigate('/dashboard/basket/redeem/place-order', {
      state: {
        basket,
      },
    })
  }

  switch (orders.state) {
    case 'loading':
      return <MFInvestmentSkeleton />
    case 'hasValue':
      return (
        <div className='py-2'>
          <div className='mb-4 px-4 lg:px-0'>
            {basketHoldings.length > 0 && (
              <div className='w-full rounded-lg border border-black-100 bg-white px-4 py-4 shadow-[0_8px_24px_rgba(4,3,19,0.05)] lg:mx-auto lg:max-w-2xl lg:px-6'>
                <div className='grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-4 sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)_auto_minmax(0,1fr)] sm:gap-6'>
                  <div className='min-w-0 space-y-1'>
                    <p className='truncate text-base font-semibold text-primary-500 sm:text-lg'>
                      ₹{' '}
                      {toCurrency(
                        basketHoldings.reduce((sum, item) => sum + item.netInvestedAmount, 0) || 0,
                      )}
                    </p>

                    <p className='text-xs leading-4 text-black-400 sm:text-sm'>Invested</p>
                  </div>
                  <Separator orientation='vertical' className='h-11' />
                  <div className='min-w-0 space-y-1 text-right sm:text-left'>
                    <p className='truncate text-base font-semibold text-primary-500 sm:text-lg'>
                      ₹{' '}
                      {toCurrency(
                        basketHoldings.reduce(
                          (sum, item) => sum + item.netUnits * item.currentNav,
                          0,
                        ) || 0,
                      )}
                    </p>
                    <p className='flex flex-wrap items-center justify-end gap-x-1 gap-y-1 text-xs leading-4 text-black-400 sm:justify-start sm:text-sm'>
                      Current{' '}
                      <span
                        className={`${getReturns(orders) < 0
                          ? 'bg-error-600 text-error-600'
                          : 'bg-secondary-600 text-secondary-600'
                          } font-semibold  rounded-[50%] h-5 w-5 flex justify-center items-center mr-1 sm:hidden`}
                      >
                        {getReturns(orders) < 0 ? (
                          <ArrowDown className='h-4 w-4 text-white' />
                        ) : (
                          <ArrowUp className='h-4 w-4 text-white' />
                        )}
                      </span>
                      <span
                        className={`${getReturns(orders) < 0 ? 'text-error-600' : 'text-secondary-600'
                          } font-semibold sm:hidden`}
                      >
                        {getReturns(orders)} %
                      </span>
                    </p>
                  </div>
                  <Separator orientation='vertical' className='h-11 hidden sm:block' />
                  <div className='space-y-1 hidden sm:block flex-1'>
                    <p className='font-semibold text-lg'>
                      ₹{' '}
                      {toCurrency(
                        (basketHoldings.reduce(
                          (sum, item) => sum + item.netUnits * item.currentNav,
                          0,
                        ) || 0) -
                        (basketHoldings.reduce((sum, item) => sum + item.netInvestedAmount, 0) ||
                          0),
                      )}
                    </p>
                    <div className='flex gap-x-1 items-center text-sm text-black-400'>
                      Returns{' '}
                      <p className='font-semibold text-secondary-600 flex items-center'>
                        <span
                          className={`${getReturns(orders) < 0
                            ? 'bg-error-600 text-error-600'
                            : 'bg-secondary-600 text-secondary-600'
                            } rounded-[50%] h-5 w-5 flex justify-center items-center text-white mr-1`}
                        >
                          {getReturns(orders) < 0 ? (
                            <ArrowDown className='h-4 w-4 text-white' />
                          ) : (
                            <ArrowUp className='h-4 w-4 text-white' />
                          )}
                        </span>
                        <span
                          className={`${getReturns(orders) < 0 ? 'text-error-600' : 'text-secondary-600'
                            } `}
                        >
                          {getReturns(orders)} %
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className='fabits-dark-section bg-white'>
            {orders?.contents?.status === 'SUCCESS' && basketHoldings.length > 0 && (
              <>
                <div className='lg:mx-auto overflow-hidden fabits-compact-card-stack'>
                  <div className='hidden md:flex justify-between py-3 pl-14 pr-2 bg-[#F4F7FE] text-primary-400 leading-5 text-sm rounded-xl'>
                    <div>Plan Name</div>

                    <div className='flex'>
                      <div className='w-[150px] text-right pr-3'>Invested</div>
                      <div className='w-[150px] text-right pr-3'>Current</div>
                      <div className='w-[150px] text-right pr-3'>Returns</div>
                    </div>
                  </div>

                  {basketHoldings.length > 0 ? (
                    Object.entries(groupedOrders).map(([key, basketOrders], index) => {
                      // const sortedOrders = [...basketOrders].sort(
                      //   (a, b) => b.customerBasketOrderNo - a.customerBasketOrderNo,
                      // )

                      // const partialOrders = basketOrders.filter(
                      //   (order) => order.basketStatus === 'PARTIAL',
                      // )

                      // const failedOrders = basketOrders.filter(
                      //   (order) => order.basketStatus === 'FAILED',
                      // )

                      return (
                        <div
                          key={key}
                          className='cursor-pointer w-full border-b border-black-100 last:border-b-0'
                        >
                          <div className='fabits-compact-card-heading py-2 text-sm font-medium text-black-400 leading-5 px-4 lg:px-0'>
                            <div
                              className='flex justify-between items-center w-full'
                              onClick={() => {
                                // Track basket holding clicked when expanding
                                const basketName = basketOrders[0]?.customerBasketName || 'Basket'
                                const totalValue = basketOrders.reduce(
                                  (sum, order) => sum + order.currentNav * order.netUnits,
                                  0,
                                )
                                PostHogTracker.basketHoldingClicked(basketName, totalValue)

                                setExpandedIndex((prevIndex) =>
                                  prevIndex === index ? null : index,
                                )
                              }}
                            >
                              <div className='w-full md:w-auto'>
                                <div className='flex md:justify-between items-start md:items-center gap-x-2'>
                                  <img
                                    src={basketRowIcon}
                                    className='fabits-basket-row-icon'
                                    alt=''
                                    aria-hidden='true'
                                  />
                                  <div className='w-full'>
                                    <div className='flex justify-between gap-x-4 items-center'>
                                      <p className='font-semibold text-gray-900  md:max-w-full text-wrap leading-6'>
                                        {basketOrders[0]?.customerBasketName}
                                      </p>

                                      <div className='flex gap-x-2 items-center'>
                                        <span className='flex-shrink-0 text-black-200 md:hidden'>
                                          <KeyboardArrowDown sx={{ fontSize: '24px' }} />
                                        </span>

                                        <Menubar>
                                          <MenubarMenu className='bg-transparent pointer-events-none'>
                                            <MenubarTrigger
                                              onClick={(e) => e.stopPropagation()}
                                              className='md:hidden hover:bg-[#F4F7FE] px-1 py-[10px] rounded-full h-6 w-6 flex justify-center items-center cursor-pointer'
                                            >
                                              <MoreVerticalIcon className='h-4' />
                                            </MenubarTrigger>

                                            <MenubarContent className='rounded-[10px] flex flex-col gap-y-1'>
                                              <MenubarItem
                                                className='pl-2 text-sm text-black-500 cursor-pointer  hover:bg-blue-100'
                                                onClick={(e) => handleMFBasketRedeem(basketOrders)}
                                              >
                                                <div>Redeem</div>
                                                {/* <AlertDialog>
                                                  <AlertDialogTrigger
                                                    asChild
                                                    onClick={(e) => e.stopPropagation()}
                                                    className='w-full'
                                                  >
                                                    <div>Redeem</div>
                                                  </AlertDialogTrigger>
                                                  <AlertDialogContent className='font-DMSans'>
                                                    <AlertDialogHeader>
                                                      <AlertDialogTitle className='text-center text-xl'>
                                                        Are you sure you want to redeem your goal ?
                                                      </AlertDialogTitle>
                                                      <AlertDialogDescription className='text-center text-base'>
                                                        This action cannot be undone. This will
                                                        process your redemption request and may
                                                        affect your savings plan.
                                                      </AlertDialogDescription>
                                                    </AlertDialogHeader>
                                                    <AlertDialogFooter>
                                                      <AlertDialogCancel>No</AlertDialogCancel>
                                                      <AlertDialogAction
                                                        onClick={() =>
                                                          handleBasketRedeem(basketOrders)
                                                        }
                                                      >
                                                        Yes
                                                      </AlertDialogAction>
                                                    </AlertDialogFooter>
                                                  </AlertDialogContent>
                                                </AlertDialog> */}
                                              </MenubarItem>
                                            </MenubarContent>
                                          </MenubarMenu>
                                        </Menubar>
                                      </div>
                                    </div>

                                    <div className='flex md:hidden gap-x-1 items-center'>
                                      {calculateParcentage(
                                        getBasketInvestedAmount(basketOrders),
                                        getBasketCurrentAmount(basketOrders),
                                      ) != 0 && (
                                          <span>
                                            {calculateParcentage(
                                              getBasketInvestedAmount(basketOrders),
                                              getBasketCurrentAmount(basketOrders),
                                            ) > 0 ? (
                                              <ArrowCircleUpOutlined
                                                style={{ height: '16px', width: '16px' }}
                                                className='text-secondary-600'
                                              />
                                            ) : (
                                              <ArrowCircleDownOutlined
                                                style={{ height: '16px', width: '16px' }}
                                                className='text-error-600'
                                              />
                                            )}
                                          </span>
                                        )}

                                      <p
                                        className={`font-semibold leading-6 ${calculateParcentage(
                                          getBasketInvestedAmount(basketOrders),
                                          getBasketCurrentAmount(basketOrders),
                                        ) > 0
                                          ? 'text-secondary-600'
                                          : calculateParcentage(
                                            getBasketInvestedAmount(basketOrders),
                                            getBasketCurrentAmount(basketOrders),
                                          ) < 0
                                            ? 'text-error-600'
                                            : 'text-black-500'
                                          }`}
                                      >
                                        {calculateParcentage(
                                          getBasketInvestedAmount(basketOrders),
                                          getBasketCurrentAmount(basketOrders),
                                        )}{' '}
                                        %
                                      </p>
                                    </div>
                                  </div>
                                </div>

                                <div className='mt-4 px-2 text-xs flex md:hidden justify-between items-center text-nowrap gap-x-4'>
                                  <div>
                                    <p className='font-semibold leading-6 text-black-500'>
                                      ₹ {toCurrency(getBasketInvestedAmount(basketOrders))}
                                    </p>
                                    <p className='text-black-400'>Invested</p>
                                  </div>
                                  <div>
                                    <p className='font-semibold leading-6 text-black-500'>
                                      ₹ {toCurrency(getBasketCurrentAmount(basketOrders))}
                                    </p>
                                    <p className='text-black-400'>Current Value</p>
                                  </div>

                                  <div>
                                    <p
                                      className={`font-semibold leading-6 ${getBasketCurrentAmount(basketOrders) -
                                        getBasketInvestedAmount(basketOrders) >
                                        0
                                        ? 'text-secondary-600'
                                        : getBasketCurrentAmount(basketOrders) -
                                          getBasketInvestedAmount(basketOrders) <
                                          0
                                          ? 'text-error-600'
                                          : 'text-black-500'
                                        }`}
                                    >
                                      ₹{' '}
                                      {toCurrency(
                                        getBasketCurrentAmount(basketOrders) -
                                        getBasketInvestedAmount(basketOrders),
                                      )}
                                    </p>
                                    <p className='text-black-400'>Gain/Loss</p>
                                  </div>
                                </div>
                              </div>

                              <div className='hidden md:flex items-center gap-x-4'>
                                <div className='flex justify-end items-center gap-x-1 min-w-[150px]'>
                                  <p className='font-semibold leading-6 text-black-500'>
                                    ₹ {toCurrency(getBasketInvestedAmount(basketOrders))}
                                  </p>
                                </div>

                                <div className='flex justify-end items-center gap-x-1 min-w-[150px]'>
                                  <p className='font-semibold leading-6 text-black-500'>
                                    ₹ {toCurrency(getBasketCurrentAmount(basketOrders))}
                                  </p>
                                </div>

                                <div className='flex justify-end items-center gap-x-1 min-w-[150px]'>
                                  <p
                                    className={`font-semibold leading-6  ${calculateParcentage(
                                      getBasketInvestedAmount(basketOrders),
                                      getBasketCurrentAmount(basketOrders),
                                    ) > 0
                                      ? 'text-secondary-600'
                                      : calculateParcentage(
                                        getBasketInvestedAmount(basketOrders),
                                        getBasketCurrentAmount(basketOrders),
                                      ) < 0
                                        ? 'text-error-600'
                                        : 'text-black-500'
                                      }`}
                                  >
                                    {calculateParcentage(
                                      getBasketInvestedAmount(basketOrders),
                                      getBasketCurrentAmount(basketOrders),
                                    )}{' '}
                                    %
                                  </p>

                                  {expandedIndex === null ? (
                                    <KeyboardArrowDown
                                      className='flex-shrink-0 text-black-200'
                                      sx={{ fontSize: '24px' }}
                                    />
                                  ) : (
                                    <KeyboardArrowUp
                                      className='flex-shrink-0 text-black-200'
                                      sx={{ fontSize: '24px' }}
                                    />
                                  )}

                                  <Menubar>
                                    <MenubarMenu className='bg-transparent pointer-events-none'>
                                      <MenubarTrigger
                                        onClick={(e) => e.stopPropagation()}
                                        className='hover:bg-[#F4F7FE] px-1 py-[10px] rounded-full h-10 w-7 flex justify-center items-center cursor-pointer'
                                      >
                                        <MoreVerticalIcon className='h-4' />
                                      </MenubarTrigger>

                                      <MenubarContent
                                        side='left'
                                        // align='left'
                                        alignOffset={8}
                                        className='rounded-[10px] flex flex-col gap-y-1'
                                      >


                                        <MenubarItem
                                          className='pl-2 text-sm text-black-500 cursor-pointer hover:bg-blue-100'
                                          // onClick={(e) => e.stopPropagation()}
                                          onClick={() => handleMFBasketRedeem(basketOrders)}
                                        >
                                          Redeem
                                          {/* <AlertDialog>
                                            <AlertDialogTrigger
                                              asChild
                                              onClick={(e) => e.stopPropagation()}
                                              className='w-full'
                                            >
                                              <div>Redeem</div>
                                            </AlertDialogTrigger>
                                            <AlertDialogContent className='font-DMSans'>
                                              <AlertDialogHeader>
                                                <AlertDialogTitle className='text-center text-xl'>
                                                  Are you sure you want to redeem your goal ?
                                                </AlertDialogTitle>
                                                <AlertDialogDescription className='text-center text-base'>
                                                  This action cannot be undone. This will process
                                                  your redemption request and may affect your
                                                  savings plan.
                                                </AlertDialogDescription>
                                              </AlertDialogHeader>
                                              <AlertDialogFooter>
                                                <AlertDialogCancel>No</AlertDialogCancel>
                                                <AlertDialogAction
                                                  onClick={() => handleBasketRedeem(basketOrders)}
                                                >
                                                  Yes
                                                </AlertDialogAction>
                                              </AlertDialogFooter>
                                            </AlertDialogContent>
                                          </AlertDialog> */}
                                        </MenubarItem>
                                      </MenubarContent>
                                    </MenubarMenu>
                                  </Menubar>
                                </div>
                              </div>
                            </div>

                            <AnimatedDisclosure
                              open={expandedIndex === index}
                              className='text-sm lg:pl-6 px-2 flex-col justify-between items-start mt-4 border-b text-gray-500'
                              contentClassName='pt-4'
                            >
                              {basketOrders?.map((order, index) => {
                                return (
                                  <div
                                    className={`py-4 flex items-center lg:items-start w-full`}
                                    key={index}
                                  >
                                    <div className='flex justify-between w-full'>
                                      <div
                                        className='flex justify-between items-center w-full'
                                        onClick={() => {
                                          setExpandedIndex((prevIndex) =>
                                            prevIndex === index ? null : index,
                                          )
                                        }}
                                      >
                                        <div className='w-full md:w-auto'>
                                          <div className='flex justify-between items-start gap-x-2'>
                                            <img
                                              src={`${env.MF_LOGOS_URL}/${order?.amcCode}.png`}
                                              className='h-12 w-12 rounded-[8px]'
                                            />
                                            <div className='w-full md:w-auto'>
                                              <p className='font-semibold text-gray-900 text-wrap md:max-w-full leading-6'>
                                                {order?.legalName
                                                  ? order?.legalName
                                                  : order?.schemeName}
                                              </p>

                                              <div className='flex justify-between md:justify-normal w-full gap-x-5'>
                                                <div className='text-xs md:text-sm flex justify-between md:justify-start gap-x-12 leading-5 flex-shrink-0 text-right w-full'>
                                                  <p className='text-black-400 leading-5 font-medium'>
                                                    NAV : {toCurrency(order.currentNav)}
                                                  </p>

                                                  {order.netUnits && (
                                                    <p className='font-medium text-black-400'>
                                                      {order.netUnits
                                                        ? toCurrency(order.netUnits)
                                                        : null}{' '}
                                                      Units
                                                    </p>
                                                  )}
                                                </div>
                                              </div>

                                              <div className='flex text-xs md:hidden justify-between items-center gap-x-2'>
                                                {order.netInvestedAmount && (
                                                  <p className='font-semibold leading-6 text-black-500'>
                                                    ₹{' '}
                                                    {order.netInvestedAmount
                                                      ? toCurrency(order.netInvestedAmount)
                                                      : null}
                                                  </p>
                                                )}

                                                {order.currentNav && order.netUnits && (
                                                  <p className='flex items-center gap-x-1 font-semibold leading-6 text-black-500'>
                                                    ₹{' '}
                                                    {toCurrency(order.currentNav * order.netUnits)}
                                                    {calculateParcentage(
                                                      order.netInvestedAmount,
                                                      order.currentNav * order.netUnits,
                                                    ) > 0 ? (
                                                      <ArrowCircleUpOutlined
                                                        style={{ height: '16px', width: '16px' }}
                                                        className='text-secondary-600'
                                                      />
                                                    ) : (
                                                      <ArrowCircleDownOutlined
                                                        style={{ height: '16px', width: '16px' }}
                                                        className='text-error-600'
                                                      />
                                                    )}
                                                  </p>
                                                )}
                                              </div>
                                            </div>
                                          </div>
                                        </div>

                                        <div className='flex items-center gap-x-1'>
                                          <div className='hidden md:flex justify-end items-center gap-x-1 min-w-[150px] pr-4'>
                                            <p className='font-semibold leading-6 text-black-500'>
                                              ₹ {toCurrency(order.netInvestedAmount)}
                                            </p>
                                          </div>

                                          <div className='hidden md:flex justify-end items-center gap-x-1 min-w-[150px]'>
                                            <p className='font-semibold leading-6 text-black-500'>
                                              ₹ {toCurrency(order.currentNav * order.netUnits)}
                                            </p>
                                          </div>

                                          <div className='hidden md:flex justify-end items-center gap-x-1 min-w-[150px] pr-4'>
                                            <p
                                              className={`font-semibold leading-6 ${calculateParcentage(
                                                order.netInvestedAmount,
                                                order.currentNav * order.netUnits,
                                              ) > 0
                                                ? 'text-secondary-600'
                                                : calculateParcentage(
                                                  order.netInvestedAmount,
                                                  order.currentNav * order.netUnits,
                                                ) < 0
                                                  ? 'text-error-600'
                                                  : 'text-black-500'
                                                }`}
                                            >
                                              {calculateParcentage(
                                                order.netInvestedAmount,
                                                order.currentNav * order.netUnits,
                                              )}{' '}
                                              %
                                            </p>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )
                              })}
                            </AnimatedDisclosure>
                          </div>
                        </div>
                      )
                    })
                  ) : (
                    <div className='fabits-dark-section flex items-center justify-center bg-white'>
                      <ApiErrorState>
                        <div className='fabits-api-error-actions'>
                          <Button
                            className='w-full'
                            variant='fabitsWhite'
                            onClick={() => window.location.reload()}
                          >
                            <Refresh className='mr-2 h-4 w-4 text-black-300' /> Refresh
                          </Button>

                          <Link className='flex-1' to='/dashboard/mutual-funds'>
                            <Button className='w-full'>
                              View Funds
                              <MoveRight className='ml-2 h-4 w-4' />
                            </Button>
                          </Link>
                        </div>
                      </ApiErrorState>
                    </div>
                  )}
                </div>
              </>
            )}

            {orders?.contents?.status === 'SUCCESS' &&
              (orders?.contents?.length === 0 || basketHoldings.length === 0) && (
                <div className='fabits-dark-section flex items-center justify-center bg-white'>
                  <Card className='w-full max-w-2xl text-center border-none shadow-none'>
                    <CardHeader>
                      <img
                        className='mx-auto h-auto w-[220px] max-w-[64vw] sm:w-[240px]'
                        src={emptyStateImage}
                        alt=''
                        aria-hidden='true'
                        draggable='false'
                      />
                      <CardTitle className='text-xl'>You haven’t set any goals yet</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className='text-sm text-black-400 mb-4'>
                        Take the first step towards your financial future by setting up a goal
                        today.
                      </p>
                      {/* <Link to='/dashboard/plans'>
                        <Button className='w-full'>
                          <Plus className='mr-2 h-4 w-4' /> Start Your Goal
                        </Button>
                      </Link> */}
                    </CardContent>
                  </Card>
                </div>
              )}

            {orders?.contents?.status === 'FAILURE' && (
              <div className='fabits-dark-section flex items-center justify-center bg-white'>
                <ApiErrorState>
                  <div className='fabits-api-error-actions'>
                    <Button
                      className='w-full'
                      variant='fabitsWhite'
                      onClick={() => window.location.reload()}
                    >
                      <Refresh className='mr-2 h-4 w-4 text-black-300' /> Refresh
                    </Button>

                    <Link className='flex-1' to='/dashboard/mutual-funds'>
                      <Button className='w-full'>
                        View Funds
                        <MoveRight className='ml-2 h-4 w-4' />
                      </Button>
                    </Link>
                  </div>
                </ApiErrorState>
              </div>
            )}
          </div>
        </div>
      )

    case 'hasError':
      return (
        <div className='fabits-dark-section flex items-center justify-center bg-white'>
          <ApiErrorState />
        </div>
      )

    default:
      return null
  }
}

export const Boilerplate = ({ state, desc }) => {
  return (
    <div className='h-3/5 flex items-center justify-center p-4'>
      <Card className='w-full max-w-sm text-center border-none shadow-none'>
        <CardHeader>
          <img className='h-14 w-14' src={`${env.SVGS}/error-icon.svg`} alt='Error-Icon' />
          <div className='w-20 h-20 mx-auto mb-4 flex justify-center items-center'>
            <img src={`${env.SVGS}/error-icon.svg`} />
          </div>
          <CardTitle className='text-xl'>{state}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className='text-muted-foreground mb-6'>{desc}</p>
          {/* <Link to='/dashboard/mutual-funds'>
            <Button className='w-full'>
              <Plus className='mr-2 h-4 w-4' /> Create SIP
            </Button>
          </Link> */}
        </CardContent>
      </Card>
    </div>
  )
}

export default MFBasketsInvestments
